import apiClient from "../clients/apiClient";
import { ApiResponse } from "../schemas/apiResponseSchema";

export interface StockInitBatch {
    id: number;
    init_no: string;
    location: string;
    init_date: string | null;
    group_codes: string[];
    processed: boolean;
    initialized_lot_count: number;
    processed_note_count: number;
}

export interface StockInitStatus {
    location: string;
    location_name: string;
    open_init: StockInitBatch | null;
}

export interface AdjustmentNoteSummary {
    doc_no: string;
    date: string | null;
    created_by: string | null;
    line_count: number;
    total_qty: number;
}

export interface AdjustmentItemPreview {
    item_code: string;
    lot: string | null;
    description: string | null;
    group: string | null;
    sih_image: number;
    current_sih: number;
    counted: number;
    variance: number;
    new_sih: number;
}

export interface AdjustmentProcessPreview extends StockInitStatus {
    notes: AdjustmentNoteSummary[];
    items: AdjustmentItemPreview[];
    draft_note_count: number;
}

export interface AdjustmentProcessResult {
    location: string;
    init_no: string;
    adjusted_lot_count: number;
    released_lot_count: number;
}

const requestOptions = { headers: { "x-skip-auth-redirect": "true" } };

const toError = (error: any, fallback: string): Error => {
    if (error?.response?.status === 401) {
        return new Error("Not authorized. Please sign in again.");
    }
    if (error?.response?.status === 403) {
        return new Error("You do not have permission to post stock adjustments.");
    }
    const detail = error?.response?.data?.detail;
    if (typeof detail === "string" && detail) {
        return new Error(detail);
    }
    return new Error(fallback);
};

export const getStockInitStatus = async (): Promise<StockInitStatus> => {
    try {
        const response: ApiResponse<StockInitStatus> = (
            await apiClient.get("/stock_adjustment/init/status", requestOptions)
        ).data;
        return response.data as StockInitStatus;
    } catch (error: any) {
        throw toError(error, "Failed to load stock initialization status");
    }
};

export const initializeStock = async (groupCodes: string[], allGroups: boolean): Promise<StockInitStatus> => {
    try {
        const response: ApiResponse<StockInitStatus> = (
            await apiClient.post(
                "/stock_adjustment/init",
                { group_codes: groupCodes, all_groups: allGroups },
                requestOptions,
            )
        ).data;
        return response.data as StockInitStatus;
    } catch (error: any) {
        throw toError(error, "Failed to initialize stock");
    }
};

export const cancelStockInit = async (initNo: string): Promise<StockInitStatus> => {
    try {
        const response: ApiResponse<StockInitStatus> = (
            await apiClient.post("/stock_adjustment/init/cancel", { init_no: initNo }, requestOptions)
        ).data;
        return response.data as StockInitStatus;
    } catch (error: any) {
        throw toError(error, "Failed to cancel stock initialization");
    }
};

export const getAdjustmentProcessPreview = async (): Promise<AdjustmentProcessPreview> => {
    try {
        const response: ApiResponse<AdjustmentProcessPreview> = (
            await apiClient.get("/stock_adjustment/process/preview", requestOptions)
        ).data;
        return response.data as AdjustmentProcessPreview;
    } catch (error: any) {
        throw toError(error, "Failed to load adjustment preview");
    }
};

export const processAdjustment = async (initNo: string): Promise<AdjustmentProcessResult> => {
    try {
        const response: ApiResponse<AdjustmentProcessResult> = (
            await apiClient.post("/stock_adjustment/process", { init_no: initNo }, requestOptions)
        ).data;
        return response.data as AdjustmentProcessResult;
    } catch (error: any) {
        throw toError(error, "Failed to process stock adjustment");
    }
};
